/* eslint-disable react-hooks/exhaustive-deps */
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { db } from "~/db/db";
import { useGetFunilObra } from "~pages/Funnel/hooks/useFunnel";
import { randomId } from "~pages/Funnel/utils";
import { getFirstSplitOfDate } from "~services/GlobalServices";


const normalizeDates = (item) => ({
  ...item,
  dataNegociacao: getFirstSplitOfDate({ date: item.dataNegociacao }),
  dataReajuste: getFirstSplitOfDate({ date: item.dataReajuste }),
  inicioConsumo: getFirstSplitOfDate({ date: item.inicioConsumoEstimado }),
  fimConsumo: getFirstSplitOfDate({ date: item.fimConsumoEstimado }),
});

const buildNewIdMap = (data) =>
  data.reduce((acc, cur) => {
    acc[`id-${cur.id}`] = true;
    return acc;
  }, {});

async function fetchFunilObra({ table, clienteId, filters, obrasAtivas }) {
  const data = [];
  let finished = false;

  const { grupoProduto, sku } = filters;

  await table
    .filter((e) => {
      if (clienteId && e.clienteId !== clienteId) return false;
      if (!obrasAtivas && e.numeroPedido) return false;
      if (typeof grupoProduto === "number" && grupoProduto) {
        if (e.funilObraGrupoProdutoId !== grupoProduto) return false;
        if (typeof sku === "number" && sku)
          return e.funilObraProdutoId === sku;
      }
      return true;
    })
    .each((e) => {
      data.push(e);
      if (!finished && e.dataEncerramento) finished = true;
    });

  return { data, finished };
}

async function fetchDefinedFilter({ table, clienteId }) {
  const result = {};

  await table
    .filter((e) => (clienteId ? e.clienteId === clienteId : true))
    .each((e) => {
      if (!e.funilObraGrupoProdutoId) return;

      if (!result[e.funilObraGrupoProdutoId]) {
        result[e.funilObraGrupoProdutoId] = { nome: "", options: [] };
      }

      if (e.funilObraProdutoId) {
        result[e.funilObraGrupoProdutoId].options.push(e.funilObraProdutoId);
      }
    });

  return result;
}

async function syncFunilObra(transformedData) {
  const newIds = buildNewIdMap(transformedData);
  const dataReturn = [];
  const dataDelete = [];

  await db.transaction("rw", db.funilObra, async () => {
    await db.funilObra.bulkPut(transformedData);

    await db.funilObra
      .filter((item) => {
        const keep = newIds[`id-${item.id}`];
        if (keep) {
          dataReturn.push({ ...item, id: randomId() });
        } else {
          dataDelete.push(item.id);
        }
        return !keep;
      })
      .each(() => null);

    if (dataDelete.length) {
      await db.funilObra.bulkDelete(dataDelete);
    }
  });

  return dataReturn;
}

async function updateDashboardAndNaoSalvo({ data, clienteId }) {
  await db.transaction("rw", db.funilObraDashboard, async () => {
    await db.funilObraDashboard.clear();
    await db.funilObraDashboard.bulkPut(data);
  });

  if (!clienteId) return;

  const naoSalvo = [];

  await db.funilObra
    .filter((e) => e.clienteId === Number(clienteId))
    .each((e) => naoSalvo.push({ ...e, id: randomId() }));

  if (!naoSalvo.length) return;

  await db.transaction("rw", db.funilObraNaoSalvo, async () => {
    const ids = [];
    await db.funilObraNaoSalvo
      .filter((e) => e.clienteId === Number(clienteId))
      .each((e) => ids.push(e.id));

    if (ids.length) await db.funilObraNaoSalvo.bulkDelete(ids);
    await db.funilObraNaoSalvo.bulkPut(naoSalvo);
  });
}



const useFunilObra = ({
  clienteId,
  filters,
  table = db.funilObraDashboard,
  updateDashboardData,
  obrasAtivas = false,
}) => {
  const [enabled] = useState(true);
  const [funisObra, setFunisObra] = useState([]);
  const [definedFilter, setDefinedFilter] = useState({});
  const [workFinished, setWorkFinished] = useState(false);

  const funilRequest = useGetFunilObra(enabled);



  useLiveQuery(() => {
    fetchFunilObra({ table, clienteId, filters, obrasAtivas }).then(
      ({ data, finished }) => {
        setFunisObra(data);
        setWorkFinished(finished);
      }
    );
  }, [filters]);



  useEffect(() => {
    fetchDefinedFilter({ table, clienteId }).then(setDefinedFilter);
  }, []);



  async function resetFunil({ data }) {
    try {
      const transformed = data.map(normalizeDates);
      return await syncFunilObra(transformed);
    } catch (error) {
      console.error("Erro ao atualizar os dados:", error);
      return [];
    }
  }

  useEffect(() => {
    if (!funilRequest.data) return;

    resetFunil({ data: funilRequest.data }).then((data) => {
      if (updateDashboardData) {
        updateDashboardAndNaoSalvo({ data, clienteId });
      }
    });
  }, [funilRequest.data]);

  return {
    setWorkFinished,
    workFinished,
    funisObra,
    resetFunil,
    definedFilter,
  };
};

export default useFunilObra;
